<template>
  <div class="arrange-mode-layer" @mousedown="onMousedown" @mouseup="onMouseup" @mousemove="onMouseMove">
    <key-frame
      @waypointSelect="onWaypointSelect" 
      @waypointMove="onWaypointMove"
      @waypointResize="onWaypointResize"
      @waypointDelete="onWaypointDelete"
      @waypointCreate="onWaypointCreate"
      @applySizeAndPosition="onApplySizeAndPosition"/>
    <cling-grid/>
    <div v-if="selecting" class="arrange-mode-layer__selection-rectangle" :style="selectionRectangleStyle"></div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import keyFrame from './keyFrame'
import clingGrid from '../../core/clingGrid'
import { getFilter } from '../../../utils/filters'

export default {
  name: 'arrangeModeLayer',

  components: {
    'key-frame' : keyFrame,
    'cling-grid': clingGrid
  },

  data() {
    return {
      snapOnGrid: false,
      isCtrl: false,
      selecting: false,
      playerXY: {},
      selectionRectangle: {
        startX: 0,
        startY: 0,
        x: 0,
        y: 0,
        width: 0,
        height: 0
      }
    }
  },

  computed: {
    ...mapState(['keyFrame', 'video']),
    ...mapGetters(['currentTime', 'getAllWaypointsByTimeAndTags']),

    allWaypoints: function () {
      const allWaypoints = this.keyFrame.selectedWaypoints.map(swp => {
        return swp.wp
      })

      return allWaypoints
    },

    selectionRectangleStyle: function () {
      return {
        left: `${this.selectionRectangle.x}px`,
        top:  `${this.selectionRectangle.y}px`,
        width: `${this.selectionRectangle.width}px`,
        height: `${this.selectionRectangle.height}px`
      }
    }
  },

  watch: {
    currentTime: function (time) {
      this.initKeyFrame(time)
    }
  },

  created() {
    this.keyEventUnsubscribe = this.keyEventSubscribe(['editor'], {
      'alt+Alt': this.onAltKeyDown,
      'Alt+up': this.onAltKeyUp,
      'ctrl+Control': this.onCtrlKeyDown,
      'Control+up': this.onCtrlKeyUp,
      'ctrl+ArrowUp': this.moveOnePxUp,
      'ctrl+ArrowDown': this.moveOnePxDown,
      'ctrl+ArrowRight': this.moveOnePxRight,
      'ctrl+ArrowLeft': this.moveOnePxLeft
    })
  },

  beforeDestroy() {
    this.keyEventUnsubscribe()
  },

  methods: {
  ...mapMutations(['deleteWaypoint', 'clearKeyFrame', 'updateKeyFrame']),

    initKeyFrame: function (time) {
      this.clearKeyFrame()
      this.getAllWaypointsByTimeAndTags(time, getFilter('1')).forEach(wp => {
        this.updateKeyFrame(wp)
      })
    },

    isWaypointInSelection: function (waypoint, selection) {
      const videoWidth = this.video.dimensions.width
      const videoHeight = this.video.dimensions.height

      const rect1 = {...selection}
      const rect2 = {
        x: (waypoint.x - (waypoint.pw / 2)) * videoWidth,
        y: (waypoint.y - (waypoint.ph / 2)) * videoHeight,
        width: waypoint.pw * videoWidth,
        height: waypoint.ph * videoHeight
      }

      if (rect1.x < rect2.x + rect2.width &&
        rect1.x + rect1.width > rect2.x &&
        rect1.y < rect2.y + rect2.height &&
        rect1.y + rect1.height > rect2.y) {
          return true
      } else {
        return false
      }
    },

    onMousedown: function (event) {
      this.selecting = true

      this.playerXY.x = event.clientX - event.offsetX
      this.playerXY.y = event.clientY - event.offsetY

      this.selectionRectangle.startX = event.offsetX
      this.selectionRectangle.startY = event.offsetY
    },

    onMouseMove: function (event) {
      if (this.selecting) {
        const rectangle = this.selectionRectangle
        const offsetX = event.clientX - this.playerXY.x
        const offsetY = event.clientY - this.playerXY.y

        if ((offsetX - rectangle.startX < 0)) {
          rectangle.width = rectangle.startX - offsetX
          rectangle.x = offsetX
        } else {
          rectangle.width = offsetX - rectangle.startX
          rectangle.x = rectangle.startX
        }

        if ((offsetY - rectangle.startY < 0)) {
          rectangle.height = rectangle.startY - offsetY
          rectangle.y = offsetY
        } else {
          rectangle.height = offsetY - rectangle.startY
          rectangle.y = rectangle.startY
        }
      }
    },

    onMouseup: function (event) {
      if (this.selecting) {
        const rectangle = this.selectionRectangle

        this.clearKeyFrame()

        this.getAllWaypointsByTimeAndTags(this.currentTime, getFilter('1')).forEach(wp => {
          if(this.isWaypointInSelection(wp.wp, rectangle)) {
            this.updateKeyFrame(wp)
          }
        })
      }

      this.selectionRectangle = { startX: 0, startY: 0, x:0, y:0, width:0, height:0 }
      this.selecting = false
    },

    onAltKeyDown: function(event) {
      event.preventDefault()
      this.snapOnGrid = true
    },

    onAltKeyUp: function(event) {
      event.preventDefault()
      this.snapOnGrid = false
    },

    onCtrlKeyDown: function(event) {
      event.preventDefault()
      this.isCtrl = true
    },

    onCtrlKeyUp: function(event) {
      event.preventDefault()
      this.isCtrl = false
    },

    moveOnePxUp: function(event) {
      event.preventDefault()
      this.moveAllWaypoints(0, -1, this.allWaypoints)
    },

    moveOnePxDown: function(event) {
      event.preventDefault()
      this.moveAllWaypoints(0, 1, this.allWaypoints)
    },

    moveOnePxRight: function(event) {
      event.preventDefault()
      this.moveAllWaypoints(1, 0, this.allWaypoints)
    },

    moveOnePxLeft: function(event) {
      event.preventDefault()
      this.moveAllWaypoints(-1, 0, this.allWaypoints)
    },

    onWaypointSelect: function (waypoint) {
      this.updateKeyFrame(waypoint)
    },

    onWaypointMove: function (event) {
      if (this.isCtrl) {
        const videoWidth = this.video.dimensions.width
        const videoHeight = this.video.dimensions.height

        const deltaX = Math.round(event.x - (event.waypoint.x - (event.waypoint.pw / 2)) * videoWidth)
        const deltaY = Math.round(event.y - (event.waypoint.y - (event.waypoint.ph / 2)) * videoHeight)

        this.moveAllWaypoints(deltaX, deltaY, this.allWaypoints)
      } else {
        this.setWaypoint(event.x, event.y, event.waypoint)
      }
    },

    onWaypointResize: function (event) {
      const minSize = 32
      const videoWidth = this.video.dimensions.width
      const videoHeight = this.video.dimensions.height

      const width = event.width
      const height = event.height
      const waypoint = event.waypoint

      waypoint.h = height > minSize ? height : minSize
      waypoint.w = width > minSize ? width : minSize
      waypoint.ph = height / videoHeight
      waypoint.pw = width / videoWidth
    },

    onWaypointDelete: function (waypoint) {
      const wp = waypoint.wp

      if (waypoint.isActive) {
        this.deleteWaypoint(wp)
      }
    },
 
    onWaypointCreate: function (waypoint) {
      const wp = waypoint.wp
      const scene = waypoint.scene

      if (scene.waypoints) {
        scene.waypoints = [...scene.waypoints, wp].sort( (a, b) => {
          return a.time - b.time
        })
      }
    },

    onApplySizeAndPosition: function (waypoint) {
      const wp = waypoint.wp
      const scene = waypoint.scene

      const pw = wp.pw
      const ph = wp.ph
      const w = wp.w
      const h = wp.h
      const x = wp.x
      const y = wp.y

      if (scene) {
        scene.waypoints.forEach((swp) => {
          swp.pw = pw
          swp.ph = ph
          swp.w = w
          swp.h = h
          swp.x = x
          swp.y = y
        })
      }
    },

    setWaypoint: function (x, y, waypoint) {
      const videoWidth = this.video.dimensions.width
      const videoHeight = this.video.dimensions.height

      waypoint.x = (x / videoWidth) + (waypoint.pw / 2)
      waypoint.y = (y / videoHeight) + (waypoint.ph / 2)
    },

    moveOneWaypoint: function (deltaX, deltaY, waypoint) {
      const videoWidth = this.video.dimensions.width
      const videoHeight = this.video.dimensions.height

      waypoint.x = (waypoint.x * videoWidth  + deltaX) / videoWidth
      waypoint.y = (waypoint.y * videoHeight  + deltaY) / videoHeight
    },

    moveAllWaypoints: function (deltaX, deltaY, waypoints) {
      waypoints.forEach(waypoint => {
        this.moveOneWaypoint(deltaX, deltaY, waypoint)
      })
    }
  }
}
</script>
